import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FiGlobe, FiCheck, FiChevronDown } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  ];

  const isRTL = i18n.language == 'ar';

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);

    // Update document direction for RTL languages
    if (langCode === 'ar') {
      document.documentElement.dir = 'rtl';
      document.documentElement.classList.add('rtl');
    } else {
      document.documentElement.dir = 'ltr';
      document.documentElement.classList.remove('rtl');
    }

    setIsOpen(false);

    // Store language preference
    localStorage.setItem('preferredLanguage', langCode);
  };

  useEffect(() => {
    // Set initial language from localStorage if available
    const storedLanguage = localStorage.getItem('preferredLanguage');
    if (storedLanguage && storedLanguage !== i18n.language) {
      changeLanguage(storedLanguage);
    }

    // Set initial direction
    if (i18n.language === 'ar') {
      document.documentElement.dir = 'rtl';
      document.documentElement.classList.add('rtl');
    }

    // Close dropdown when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [i18n]);

  return (
    <div className="relative " ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className="flex items-center cursor-pointer gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600 transition-all duration-200"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <FiGlobe className="w-4 h-4 text-primary" />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
          {currentLanguage.flag}
        </span>
        <FiChevronDown className={`w-3.5 h-3.5 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className={`absolute z-50 ${isRTL ? 'left-0' : 'right-0'} mt-2 w-44 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden`}
          >
            <div className="p-1.5" role="menu" aria-orientation="vertical">
              {languages.map((language) => {
                const isActive = currentLanguage.code === language.code;
                return (
                  <button
                    key={language.code}
                    onClick={() => changeLanguage(language.code)}
                    className={`w-full cursor-pointer text-start px-3 py-2.5 rounded-lg flex items-center gap-3 transition-all duration-150 border ${isActive
                      ? 'bg-gray-100 dark:bg-gray-700 border-primary text-primary'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700/50 border-transparent'
                    }`}
                    role="menuitem"
                  >
                    <span className="text-2xl">{language.flag}</span>
                    <div className="flex-1">
                      <span className={`text-sm font-medium ${isActive ? 'text-primary' : 'text-gray-800 dark:text-gray-100'}`}>
                        {language.name}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSwitcher;